import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { useUser } from "../UserContext";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

interface PaymentMethod {
  id: string;
  card: {
    last4: string;
    brand: string;
    exp_month: number;
    exp_year: number;
  };
  isDefault: boolean;
}

interface BillingHistory {
  id: string;
  amount: number;
  status: string;
  date: string;
  description: string;
  paymentMethod: {
    brand: string;
    last4: string;
  };
  refunded: boolean;
  refundAmount: number;
  receiptUrl: string | null;
  paymentIntentId: string;
}

const PaymentForm: React.FC<{
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { setupIntent, error: submitError } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}/settings/payments`,
        },
      });

      if (submitError) {
        setError(submitError.message || "An error occurred");
      } else if (setupIntent && setupIntent.status === "succeeded") {
        onSuccess();
      } else {
        setError("Payment setup failed. Please try again.");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <DialogActions sx={{ mt: 2 }}>
        <Button onClick={onCancel} disabled={processing}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || processing}
        >
          {processing ? "Processing..." : "Add Card"}
        </Button>
      </DialogActions>
    </form>
  );
};

const Payments: React.FC = () => {
  const { currentUser } = useUser();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [billingHistory, setBillingHistory] = useState<BillingHistory[]>([]);
  const [hasMorePayments, setHasMorePayments] = useState(false);
  const [openAddCard, setOpenAddCard] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  useEffect(() => {
    if (currentUser?.user_id) {
      fetchPaymentMethods();
      fetchBillingHistory();
    }
  }, [currentUser?.user_id]);

  const fetchPaymentMethods = async () => {
    if (!currentUser?.user_id) return;
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/payments/methods`,
        {
          params: { userId: currentUser.user_id },
        }
      );

      // Check if response.data exists and is an array
      if (Array.isArray(response.data)) {
        // Transform the Stripe payment method data to match our interface
        const transformedMethods = response.data.map((method: any) => ({
          id: method.id,
          card: {
            last4: method.card.last4,
            brand: method.card.brand,
            exp_month: method.card.exp_month,
            exp_year: method.card.exp_year,
          },
          isDefault: method.isDefault || false,
        }));
        setPaymentMethods(transformedMethods);
      } else {
        console.error("Unexpected response format:", response.data);
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      setPaymentMethods([]);
    }
  };

  const fetchBillingHistory = async () => {
    if (!currentUser?.user_id) return;
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/payments/history`,
        {
          params: { userId: currentUser.user_id },
        }
      );
      setBillingHistory(response.data.payments || []);
      setHasMorePayments(response.data.has_more || false);
    } catch (error) {
      console.error("Error fetching billing history:", error);
      setBillingHistory([]);
    }
  };

  const handleAddCard = async () => {
    if (!currentUser?.user_id) {
      console.log("No user ID found");
      return;
    }
    console.log("Starting to add card...");
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/payments/setup-intent`,
        {
          userId: currentUser.user_id,
        }
      );
      console.log("Setup intent response:", response.data);

      if (response.data.clientSecret) {
        console.log("Setting client secret and opening dialog");
        setClientSecret(response.data.clientSecret);
        setOpenAddCard(true);
      } else {
        console.error("No client secret received");
      }
    } catch (error) {
      console.error("Error setting up payment:", error);
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        // Show rate limit error message
        alert("Too many requests. Please wait a moment and try again.");
      } else {
        alert(
          "An error occurred while setting up the payment. Please try again."
        );
      }
    }
  };

  const handleRemoveCard = async (paymentMethodId: string) => {
    try {
      await axios.delete(
        `${
          import.meta.env.VITE_API_URL
        }/api/payments/methods/${paymentMethodId}`
      );
      setPaymentMethods((methods) =>
        methods.filter((method) => method.id !== paymentMethodId)
      );
    } catch (error) {
      console.error("Error removing payment method:", error);
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      await axios.post(
        `${
          import.meta.env.VITE_API_URL
        }/api/payments/methods/${paymentMethodId}/default`
      );
      setPaymentMethods((methods) =>
        methods.map((method) => ({
          ...method,
          isDefault: method.id === paymentMethodId,
        }))
      );
    } catch (error) {
      console.error("Error setting default payment method:", error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Grid container spacing={3}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Typography variant="h4" gutterBottom>
              Payment Methods
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Manage your payment methods for event bookings
            </Typography>
            <Divider sx={{ mb: 4 }} />
          </Grid>

          {/* Payment Methods List */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {paymentMethods.map((method) => (
                <Grid item xs={12} md={6} key={method.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: method.isDefault
                        ? "primary.main"
                        : "divider",
                    }}
                  >
                    <CardContent>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                          <CreditCardIcon
                            sx={{
                              fontSize: 40,
                              color: method.isDefault
                                ? "primary.main"
                                : "action.active",
                            }}
                          />
                        </Grid>
                        <Grid item xs>
                          <Typography variant="h6">
                            {method.card.brand.charAt(0).toUpperCase() +
                              method.card.brand.slice(1)}{" "}
                            x{method.card.last4}
                          </Typography>
                          <Typography color="text.secondary">
                            Expires{" "}
                            {String(method.card.exp_month).padStart(2, "0")}/
                            {method.card.exp_year}
                          </Typography>
                        </Grid>
                        <Grid item>
                          {!method.isDefault && (
                            <>
                              <Button
                                size="small"
                                onClick={() => handleSetDefault(method.id)}
                                sx={{ mr: 1 }}
                              >
                                Set Default
                              </Button>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveCard(method.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                          {method.isDefault && (
                            <Typography
                              color="primary"
                              sx={{
                                fontSize: "0.875rem",
                                fontWeight: 500,
                              }}
                            >
                              Default
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

              {/* Add New Card Button */}
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                  onClick={handleAddCard}
                >
                  <CardContent>
                    <Box sx={{ textAlign: "center" }}>
                      <AddIcon
                        sx={{ fontSize: 40, color: "action.active", mb: 1 }}
                      />
                      <Typography>Add New Payment Method</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Billing History Section */}
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Billing History
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Payment Method</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Receipt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">
                          No payment history available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    billingHistory.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          {new Date(item.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>
                          {item.paymentMethod &&
                            `${
                              item.paymentMethod.brand.charAt(0).toUpperCase() +
                              item.paymentMethod.brand.slice(1)
                            } x${item.paymentMethod.last4}`}
                        </TableCell>
                        <TableCell align="right">
                          ${(item.amount / 100).toFixed(2)}
                          {item.refunded && (
                            <Typography
                              component="span"
                              color="error.main"
                              sx={{ ml: 1 }}
                            >
                              (Refunded: ${(item.refundAmount / 100).toFixed(2)}
                              )
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            color={
                              item.status === "succeeded"
                                ? "success.main"
                                : item.status === "pending"
                                ? "warning.main"
                                : "error.main"
                            }
                          >
                            {item.status.charAt(0).toUpperCase() +
                              item.status.slice(1)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.receiptUrl && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                item.receiptUrl &&
                                window.open(item.receiptUrl, "_blank")
                              }
                            >
                              <ReceiptIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {hasMorePayments && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography color="text.secondary" variant="body2">
                  Showing most recent transactions. Contact support to view
                  older transactions.
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Security Note */}
          <Grid item xs={12}>
            <Alert icon={<LockIcon />} severity="info" sx={{ mt: 3 }}>
              Your payment information is securely stored and processed by
              Stripe. We never store your full card details on our servers.
            </Alert>
          </Grid>
        </Grid>
      </Paper>

      {/* Add Card Dialog */}
      <Dialog
        open={openAddCard}
        onClose={() => setOpenAddCard(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Payment Method</DialogTitle>
        <DialogContent>
          {clientSecret && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: "stripe",
                },
              }}
            >
              <PaymentForm
                onSuccess={() => {
                  setOpenAddCard(false);
                  setClientSecret(null);
                  fetchPaymentMethods();
                }}
                onCancel={() => {
                  setOpenAddCard(false);
                  setClientSecret(null);
                }}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Payments;
